import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';
import { ArrowLeft, Mail } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = window.__BACKEND_URL__;
const API = `${BACKEND_URL}/api`;

const ForgotPassword = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [emailSent, setEmailSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      
      setEmailSent(true);
      toast({
        title: 'Temporary Password Sent',
        description: 'If your email is registered, you will receive a temporary password. Use it to sign in and you\'ll be prompted to create a new password.',
      });
    } catch (error) {
      let errorMessage = 'Failed to send reset email';
      
      // Check if this is a network error (offline or backend unreachable)
      if (!error.response) {
        errorMessage = 'Cannot connect to server. Please check your internet connection and try again.';
      } else {
        // Use the backend's error message if available
        errorMessage = error.response?.data?.detail || error.response?.data?.message || errorMessage;
      }
      
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-green-50 p-4">
      <Card className="w-full max-w-md shadow-2xl border-2">
        <CardHeader className="space-y-4 text-center">
          <div className="flex justify-center mb-4">
            <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-green-500 rounded-full flex items-center justify-center">
              <Mail className="w-10 h-10 text-white" />
            </div>
          </div>
          <div>
            <CardTitle className="text-3xl font-bold text-white">Forgot Password?</CardTitle>
            <CardDescription className="text-base mt-2">
              {emailSent 
                ? 'Check your email for your temporary password'
                : 'Enter your email to receive a temporary password'
              }
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent className="px-8 pb-8">
          {!emailSent ? (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="Enter your registered email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="h-11"
                />
              </div>
              <Button 
                type="submit" 
                className="w-full h-12 bg-gradient-to-r from-blue-600 to-green-600 hover:from-blue-700 hover:to-green-700 text-white font-semibold transition-all"
                disabled={isLoading}
              >
                {isLoading ? 'Sending...' : 'Send Temporary Password'}
              </Button>
            </form>
          ) : (
            <div className="space-y-5">
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-800">
                  <strong>Temporary password sent!</strong> If an account exists with the email <strong>{email}</strong>, 
                  you will receive a temporary password via email shortly.
                </p>
              </div>
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Next Steps:</strong><br/>
                  1. Check your email for the temporary password<br/>
                  2. Sign in with the temporary password<br/>
                  3. You'll be prompted to create a new password of your choosing
                </p>
              </div>
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  <strong>Didn't receive the email?</strong> Check your spam folder or try again in a few minutes.
                </p>
              </div>
              <Button
                onClick={() => navigate('/signin')}
                className="w-full h-11 bg-[#1E88E5] hover:bg-blue-700"
              >
                Return to Sign In
              </Button>
            </div>
          )}
          
          {!emailSent && (
            <div className="mt-6">
              <Button
                onClick={() => navigate('/signin')}
                variant="ghost"
                className="w-full"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Sign In
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ForgotPassword;